import { useCallback, useEffect, useState } from 'react';

import { useLocation } from 'react-router-dom';

import useAppNavigation from '~/hooks/useAppNavigation';

import * as toursService from '~/services/ToursService';

import { parseSearchString } from '~/utils/url';

import { TourDealPageBase, TourDealPageListQuery } from './types';

interface Filter {
  name?: string;
  cruiseLine?: string;
  rateCodes?: string;
}

interface TourDealsData {
  dealsPage: Array<TourDealPageBase>;
  page: number;
  total: number;
  isLoading: boolean;
  recordsPerPage: number;
  paginate: (page: number) => void;
  setFilter: (value: Filter) => void;
  filter: Filter;
  getDealsPage: (params?: TourDealPageListQuery) => Promise<void>;
}

const RECORDS_PER_PAGE = 15;

export interface Props {
  isActive?: boolean;
}

export default function useTourDealsPage({ isActive }: Props): TourDealsData {
  const [filter, setFilter] = useState<Filter>({});
  const { search } = useLocation();
  const { push: setQueryString } = useAppNavigation();
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [dealsPage, setDealsPage] = useState<Array<TourDealPageBase>>([]);
  const [isLoading, setIsLoading] = useState(false);

  const getDealsPage = useCallback(async () => {
    try {
      setIsLoading(true);
      const res = await toursService.getTourDeals(page, RECORDS_PER_PAGE);
      setDealsPage(res);
      setTotal(res.length);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  }, [page]);

  const setCurrentPage = useCallback((): void => {
    const queryString = parseSearchString(search);
    if (queryString) {
      const pageToGo = parseInt(queryString.page as string);
      if (pageToGo) {
        setPage(pageToGo);
      }
    }
  }, [search]);

  const paginate = useCallback(
    (page: number): void => {
      const queryString = new URLSearchParams({
        page: page.toString(),
      }).toString();
      setQueryString({ search: queryString });
      setPage(page);
    },
    [setQueryString],
  );

  useEffect(() => {
    setCurrentPage();
  }, [setCurrentPage]);

  useEffect(() => {
    getDealsPage();
  }, [getDealsPage]);

  return {
    filter,
    setFilter,
    dealsPage,
    getDealsPage,
    page,
    recordsPerPage: RECORDS_PER_PAGE,
    paginate,
    total,
    isLoading,
  };
}
